import {NETWORK_API, NETWORK_PROFILE_API} from '../components/session/session-profile.tokens';
import {NetworkApiService} from './admin/network/network-api.service';
import {NetworkProfileApiService} from '../components/session/network-profile-api.service';
import {
  APP_BOOTSTRAP_LISTENER,
  ApplicationConfig,
  ErrorHandler,
  Injector,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideUIRouter } from '@uirouter/angular';
import { UIRouter } from '@uirouter/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {ADN_TRANSLATIONS} from "./common/translations";
import { provideQuillConfig } from 'ngx-quill/config';
import { provideToastr } from 'ngx-toastr';

import {
  DEFAULT_LANGUAGE,
  FakeMissingTranslationHandler,
  MissingTranslationHandler,
  TranslateCompiler,
  TranslateFakeCompiler,
  TranslateFakeLoader,
  TranslateLoader,
  TranslateParser,
  TranslateService,
  TranslateStore,
  USE_DEFAULT_LANG,
  USE_EXTEND,
  USE_STORE
} from '@ngx-translate/core';
import { AdnTranslateParser } from './common/filters/adn-translate-parser';
import { installMissingTranslateKeyReporting } from './common/filters/missing-translate-key';
import { AdnLogService } from '../components/util/adn-log.service';

import { localUserProfileTokenProvider, localNetworkProfileTokenProvider } from "./app-compatibility-providers";
import { adnStatFilterProvider } from './common/filters/adn-stat-filter';
import { angularPlatformProviders } from './angular-platform-providers';
import {MODAL_CONFIG_DEFAULT_OVERRIDE} from "ngx-bootstrap/modal";
import {StatPipe} from "./common/filters/stat.pipe";
import {DisplayStatPipe} from "./common/filters/display-stat.pipe";
import {CurrencyPipe, DecimalPipe, KeyValuePipe, PercentPipe} from "@angular/common";
import {OgTranslatePipe} from "./common/filters/og-translate.pipe";
import {LocaleDatePipe} from "./common/filters/locale-date.pipe";
import {LocaleNumberPipe} from "./common/filters/locale-number.pipe";
import {LocalePercentPipe} from "./common/filters/locale-percent.pipe";
import {SafeHtmlPipe} from "./common/filters/safe-html.pipe";
import {PercentagePipe} from "./common/filters/percentage.pipe";
import {AdnNumberPipe} from "./common/filters/adn-number.pipe";
import {IsoCurrencyPipe, StringToCurrencyPipe} from "./common/filters/iso-currency.pipe";

import {GuardTypePipe} from "./common/filters/guard-type.pipe";
import {MimeTypeKeyPipe} from "./common/filters/mime-type-key.pipe";
import {ReportScheduleModalService} from "./report-schedule/report-schedule-modal.service";
import { SignInAuthListenerService } from './sign-in/sign-in-auth-listener.service';
import { AppRuntimeLifecycleService } from './base/app-runtime-lifecycle.service';
import {ReleaseNotesModalService} from "./base/simple/release-notes-modal.service";
import {AngularErrorHandlerService} from "./errors/angular-error-handler.service";
import {ReleaseNotesHelper} from "../components/util/release-notes-helper.service";
import {CreativeHelper} from "./creative/creative-helper.service";
import {ChartOptionsUtil} from "./common/options/chart-options-util.service";
import {AdnInactiveTargetsService} from "../components/util/adn-inactive-targets.service";
import {DocumentShellService} from "./base/document-shell.service";
import {registerFeatureStates} from './routing/register-feature-states';
import {WORKSPACE_REGISTRY_API} from '../components/workspace-registry.service';
import {WorkspaceApiService} from './workspace/workspace-api.service';

import "ngx-toastr/toastr";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import './common/options/highcharts-defaults';
import '../style/object-diff.scss';
import '../style/app.scss';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideToastr({
      timeOut: 10000,
      preventDuplicates: true,
      enableHtml: true
    }),
    provideUIRouter({
      states: [],
      useHash: false,
      deferIntercept: true
    }),
    provideQuillConfig({}),
    {provide: TranslateLoader, useClass: TranslateFakeLoader},
    {provide: TranslateCompiler, useClass: TranslateFakeCompiler},
    {provide: TranslateParser, useClass: AdnTranslateParser},
    {provide: MissingTranslationHandler, useClass: FakeMissingTranslationHandler},
    TranslateStore,
    {provide: USE_STORE, useValue: undefined},
    {provide: USE_DEFAULT_LANG, useValue: undefined},
    {provide: USE_EXTEND, useValue: undefined},
    {provide: DEFAULT_LANGUAGE, useValue: undefined},
    TranslateService,
    {provide: NETWORK_API, useExisting: NetworkApiService},
    {provide: NETWORK_PROFILE_API, useExisting: NetworkProfileApiService},
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptorsFromDi()),
    DocumentShellService,
    ReleaseNotesHelper,
    CreativeHelper,
    ChartOptionsUtil,
    AdnInactiveTargetsService,
    ReleaseNotesModalService,
    ReportScheduleModalService,
    {
      provide: MODAL_CONFIG_DEFAULT_OVERRIDE,
      useValue: {keyboard: true}
    },
    {provide: WORKSPACE_REGISTRY_API, useExisting: WorkspaceApiService},
    localUserProfileTokenProvider,
    localNetworkProfileTokenProvider,
    {provide: ErrorHandler, useClass: AngularErrorHandlerService},
    adnStatFilterProvider,
    PercentPipe,
    DecimalPipe,
    CurrencyPipe,
    OgTranslatePipe,
    LocaleDatePipe,
    LocaleNumberPipe,
    LocalePercentPipe,
    SafeHtmlPipe,
    StatPipe,
    DisplayStatPipe,
    PercentagePipe,
    AdnNumberPipe,
    IsoCurrencyPipe,
    StringToCurrencyPipe,
    KeyValuePipe,
    GuardTypePipe,
    MimeTypeKeyPipe,
    ...angularPlatformProviders,
    provideAppInitializer(() => {
      const injector = inject(Injector);
      const translate = inject(TranslateService);
      const uiRouter = injector.get(UIRouter);
      installMissingTranslateKeyReporting(
        translate,
        injector.get(AdnLogService),
        () => ({
          href: typeof location !== 'undefined' ? location.href : undefined,
          stateName: uiRouter.globals?.current?.name
        })
      );
      translate.setDefaultLang("en");
      translate.setTranslation("en", ADN_TRANSLATIONS);
      registerFeatureStates(uiRouter, injector);
      injector.get(AppRuntimeLifecycleService).init();
      injector.get(SignInAuthListenerService);
    }),
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        const documentShell = inject(DocumentShellService);
        return (): void => documentShell.init();
      }
    }
  ]
};
